import {
  AssignableRolesResponseSchema,
  AttachCustomerResponseSchema,
  CompanyCustomersResponseSchema,
  CompanyInvitationsResponseSchema,
  CompanyMemberDetailSchema,
  CompanyPermissionsResponseSchema,
  CompanySchema,
  CreateCompanyResponseSchema,
  PaginatedCompaniesResponseSchema,
  type AssignableRolesResponse,
  type AttachCustomerRequest,
  type Company,
  type CompanyCustomer,
  type CompanyInvitation,
  type CompanyMemberDetail,
  type CompanyPermissionsResponse,
  type CreateCompanyRequest,
  type CreateInvitationRequest,
  type PaginatedActivityLogsResponse,
  type PaginatedCompaniesResponse,
  type PaginatedCustomersResponse,
  type UpdateCustomerRoleRequest,
} from '@/schemas/company';
import type { Result } from '@/types/api-routes';
import { API_ROUTES, apiRequest, validatedApiRequest } from '@/types/api-routes';

class CompanyService {
  getCompanies(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    organization_id?: string;
    organization_ids?: string[];
    sort_field?: string;
    sort_order?: string;
  }): Promise<Result<PaginatedCompaniesResponse>> {
    const queryParams: Record<string, string> = {};
    if (params?.page) {
      queryParams.page = params.page.toString();
    }
    if (params?.per_page) {
      queryParams.per_page = params.per_page.toString();
    }
    if (params?.status) {
      queryParams.status = params.status;
    }
    if (params?.organization_id) {
      queryParams.organization_id = params.organization_id;
    }
    if (params?.organization_ids && params.organization_ids.length > 0) {
      params.organization_ids.forEach((id, index) => {
        queryParams[`organization_ids[${index}]`] = id;
      });
    }
    if (params?.sort_field) {
      queryParams.sort_field = params.sort_field;
    }
    if (params?.sort_order) {
      queryParams.sort_order = params.sort_order;
    }

    return validatedApiRequest(API_ROUTES.admin.companies.list, PaginatedCompaniesResponseSchema, queryParams);
  }

  getCompany(id: string): Promise<Result<Company>> {
    return validatedApiRequest(API_ROUTES.admin.companies.show, CompanySchema, { companyId: id });
  }

  createCompany(companyData: CreateCompanyRequest): Promise<Result<Company>> {
    return validatedApiRequest(API_ROUTES.admin.companies.create, CreateCompanyResponseSchema, companyData);
  }

  updateCompany(id: string, companyData: CreateCompanyRequest): Promise<Result<Company>> {
    return validatedApiRequest(
      API_ROUTES.admin.companies.update,
      CreateCompanyResponseSchema,
      { companyId: id },
      companyData,
    );
  }

  deleteCompany(id: string): Promise<Result<void>> {
    return apiRequest(API_ROUTES.admin.companies.delete, { companyId: id });
  }

  getCompanyCustomers(companyId: string): Promise<Result<CompanyCustomer[]>> {
    return validatedApiRequest(API_ROUTES.admin.companies.customers.list, CompanyCustomersResponseSchema, {
      companyId,
    });
  }

  getAvailableCustomers(
    companyId: string,
    search?: string,
    page: number = 1,
    perPage: number = 20,
  ): Promise<Result<PaginatedCustomersResponse>> {
    const queryParams: Record<string, string> = {
      page: page.toString(),
      per_page: perPage.toString(),
    };
    if (search) {
      queryParams.search = search;
    }

    return apiRequest(API_ROUTES.admin.companies.available_customers, { companyId }, queryParams);
  }

  attachCustomer(companyId: string, request: AttachCustomerRequest): Promise<Result<CompanyCustomer>> {
    return validatedApiRequest(
      API_ROUTES.admin.companies.customers.attach,
      AttachCustomerResponseSchema,
      { companyId },
      request,
    );
  }

  getAssignableRoles(companyId: string): Promise<Result<AssignableRolesResponse>> {
    return validatedApiRequest(API_ROUTES.admin.companies.assignable_roles, AssignableRolesResponseSchema, {
      companyId,
    });
  }

  getCompanyUser(companyId: string, customerId: string): Promise<Result<CompanyMemberDetail>> {
    return validatedApiRequest(API_ROUTES.admin.companies.users.show, CompanyMemberDetailSchema, {
      companyId,
      customerId,
    });
  }

  updateCompanyUserRoles(
    companyId: string,
    customerId: string,
    payload: { role_ids: string[] },
  ): Promise<Result<unknown>> {
    return apiRequest(API_ROUTES.admin.companies.users.roles.update, { companyId, customerId }, payload);
  }

  updateCompanyUserPermissions(
    companyId: string,
    customerId: string,
    payload: { permissions: string[] },
  ): Promise<Result<unknown>> {
    return apiRequest(API_ROUTES.admin.companies.users.permissions.update, { companyId, customerId }, payload);
  }

  getCustomerPermissionsCatalog(): Promise<Result<unknown>> {
    return apiRequest(API_ROUTES.admin.companies.customer_permissions, {});
  }

  createCompanyRole(companyId: string, payload: { name: string; permissions: string[] }): Promise<Result<unknown>> {
    return apiRequest(API_ROUTES.admin.companies.roles.store, { companyId }, payload);
  }

  updateCompanyRole(
    companyId: string,
    roleId: string,
    payload: { name: string; permissions: string[] },
  ): Promise<Result<unknown>> {
    return apiRequest(API_ROUTES.admin.companies.roles.update, { companyId, roleId }, payload);
  }

  deleteCompanyRole(companyId: string, roleId: string): Promise<Result<void>> {
    return apiRequest(API_ROUTES.admin.companies.roles.destroy, { companyId, roleId });
  }

  updateCustomerRole(
    companyId: string,
    customerId: string,
    request: UpdateCustomerRoleRequest,
  ): Promise<Result<CompanyCustomer>> {
    return validatedApiRequest(
      API_ROUTES.admin.companies.customers.update,
      AttachCustomerResponseSchema,
      { companyId, customerId },
      request,
    );
  }

  detachCustomer(companyId: string, customerId: string): Promise<Result<void>> {
    return apiRequest(API_ROUTES.admin.companies.customers.detach, { companyId, customerId });
  }

  getCompanyActivityLogs(
    companyId: string,
    page: number = 1,
    perPage: number = 15,
  ): Promise<Result<PaginatedActivityLogsResponse>> {
    const queryParams: Record<string, string> = {
      page: page.toString(),
      per_page: perPage.toString(),
    };

    return apiRequest(API_ROUTES.admin.companies.activity_logs, { companyId }, queryParams);
  }

  getCompanyInvitations(companyId: string): Promise<Result<CompanyInvitation[]>> {
    return validatedApiRequest(API_ROUTES.admin.companies.invitations.index, CompanyInvitationsResponseSchema, {
      companyId,
    });
  }

  createCompanyInvitation(companyId: string, request: CreateInvitationRequest): Promise<Result<unknown>> {
    return apiRequest(API_ROUTES.admin.companies.invitations.store, { companyId }, request);
  }

  cancelCompanyInvitation(companyId: string, invitationId: string): Promise<Result<void>> {
    return apiRequest(API_ROUTES.admin.companies.invitations.destroy, {
      companyId,
      companyInvitationId: invitationId,
    });
  }

  resendCompanyInvitation(companyId: string, invitationId: string): Promise<Result<void>> {
    return apiRequest(API_ROUTES.admin.companies.invitations.resend, {
      companyId,
      companyInvitationId: invitationId,
    });
  }

  getCompanyPermissions(companyId: string): Promise<Result<CompanyPermissionsResponse>> {
    return validatedApiRequest(API_ROUTES.customer.permissions, CompanyPermissionsResponseSchema, { companyId });
  }

  getCustomerCompanySettings(companyId: string): Promise<Result<Company>> {
    return validatedApiRequest(API_ROUTES.customer.settings.show, CompanySchema, { companyId });
  }

  updateCustomerCompanySettings(companyId: string, companyData: CreateCompanyRequest): Promise<Result<Company>> {
    return validatedApiRequest(
      API_ROUTES.customer.settings.update,
      CreateCompanyResponseSchema,
      { companyId },
      companyData,
    );
  }
}

export const companyService = new CompanyService();

export {
  type AssignableRole,
  type AssignableRolesResponse,
  type Company,
  type CompanyCustomer,
  type CompanyInvitation,
  type CompanyMemberDetail,
  type CreateCompanyRequest,
  type AttachCustomerRequest,
  type UpdateCustomerRoleRequest,
  type CreateInvitationRequest,
  type PaginatedActivityLogsResponse,
  type PaginatedCompaniesResponse,
  type PaginatedCustomersResponse,
} from '@/schemas/company';

export type { ActivityLog } from '@/schemas/company';
